import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AuthLoadingView } from 'shared-ui/lib/components/auth/AuthLoadingView';
import { Details } from 'shared-ui/lib/components/Details';
import { Image } from 'shared-ui/lib/components/Image';
import { setupQueryConfig } from 'shared-ui/lib/config/query.config';
import { useParams } from 'shared-ui/lib/utils/use-params';
import { getLicenseVisualStatus } from 'shared-ui/lib/utils/license-status';
import { vehicleColumns } from '../../columns/vehicle.columns';
import { AddIconButton } from '../../components/scenes/vehicle-show-scene/AddIconButton';
import { MaintenanceListItem } from '../../components/scenes/vehicle-show-scene/maintenance-list-item/MaintenanceListItem';
import { ReminderListItem } from '../../components/scenes/vehicle-show-scene/reminder-list-item/ReminderListItem';
import { routesConfig } from '../../config/routes.config';
import { useCreateTransferTokenMutation, useVehicleShowQuery } from '../../generated/graphql';
import { TransferVehicleIcon } from '../../components/icons/TransferVehicleIcon';
import { PaymentIcon } from '../../components/icons/PaymentIcon';
import { EditVehicleIcon } from '../../components/icons/EditVehicleIcon';
import { PrintIcon } from '../../components/icons/PrintIcon';
import { UrgentClockIcon } from '../../components/icons/UrgentClockIcon';
import { LockIcon } from '../../components/icons/LockIcon';
import { VehicleAttachmentsModal } from '../../components/scenes/vehicle-show-scene/VehicleAttachmentsModal';

export const VehicleShowScene = () => {
  const { t } = useTranslation('VehicleShowScene');
  const { id } = useParams<{ id: string }>();
  const [showDetails, setShowDetails] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [isAttachmentsOpen, setIsAttachmentsOpen] = useState(false);

  const remindersRef = useRef<HTMLHeadingElement>(null);
  const serviceEventsRef = useRef<HTMLHeadingElement>(null);

  const [createTransferToken, { loading: transferTokenLoading }] = useCreateTransferTokenMutation({
    variables: { vehicleId: id },
    onCompleted: (d) => {
      if (d.createTransferToken) {
        toast.success(t('transferLinkSent'));
      }
    },
  });

  const scrollToReminders = useCallback(() => {
    if (remindersRef.current) {
      remindersRef.current.scrollIntoView();
    }
  }, [remindersRef]);

  const scrollToServiceEvents = useCallback(() => {
    if (serviceEventsRef.current) {
      serviceEventsRef.current.scrollIntoView();
    }
  }, [serviceEventsRef]);

  const columns = [
    vehicleColumns.name,
    vehicleColumns.registerNumber,
    vehicleColumns.brand,
    vehicleColumns.model,
    vehicleColumns.token,
    vehicleColumns.mileage,
    vehicleColumns.VIN,
    vehicleColumns.runningHours,
    vehicleColumns.modelYear,
    vehicleColumns.commissioning,
    vehicleColumns.warranty,
    vehicleColumns.type,
    vehicleColumns.description,
  ];

  const { data, loading, refetch } = useVehicleShowQuery(setupQueryConfig({ id }));

  if (loading || !data) return <AuthLoadingView />;

  const { vehicle } = data;

  const licenseInfo = getLicenseVisualStatus({
    licenseStatus: vehicle.licenseStatus,
    licenseExpireDate: vehicle.licenseExpireDate,
  });

  const getLicenseBadgeClasses = () => {
    const base = 'inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium';
    if (licenseInfo.level === 'NO_ACTIVE') {
      return `${base} bg-gray-500 text-white`;
    }
    if (licenseInfo.level === 'RED') {
      return `${base} bg-red-600 text-white`;
    }
    if (licenseInfo.level === 'ORANGE') {
      return `${base} bg-orange-color text-white`;
    }
    if (licenseInfo.level === 'YELLOW') {
      return `${base} bg-yellow-500 text-white`;
    }
    return `${base} bg-green-600 text-white`;
  };

  const emptyRemainder = (
    <div className="text-sm font-medium text-gray-color mt-2">
      <p>{t('emptyRemainder')}</p>
      <Link
        to={routesConfig.REMINDER.CREATE.replace(':vehicleId', id)}
        className="text-primary cursor-pointer font-bold underline"
      >
        <p className="mt-2">{t('emptyRemainderAddNew')}</p>
      </Link>
    </div>
  );

  const emptyServiceEvent = (
    <div className="text-sm font-medium text-gray-color mt-2">
      <p>{t('emptyServiceEvent')}</p>
      <Link
        to={routesConfig.SERVICE_EVENTS.CREATE.replace(':vehicleId', id)}
        className="text-primary cursor-pointer font-bold underline"
      >
        <p className="mt-2">{t('emptyServiceEventAddNew')}</p>
      </Link>
    </div>
  );

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-3 mt-1 sm:mt-0">
      <div>
        <div className="bg-white py-4 px-3 shadow rounded-lg">
          <div className="grid grid-cols-4 items-center">
            <h2 className="text-lg col-span-2 leading-6 font-bold text-primary">
              {t('vehicleInformation')}
            </h2>
            <div className="xl:flex xl:justify-end col-span-2 space-x-2">
              <div className="relative inline-block text-left">
                <button
                  type="button"
                  onClick={() => setIsActionsOpen(!isActionsOpen)}
                  className="inline-flex justify-center items-center w-full rounded-md border border-primary shadow-sm pl-3 pr-2 py-2 bg-white text-sm font-medium text-primary hover:bg-secondary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                >
                  <span>{t('actions')}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 ml-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.25 8.27a.75.75 0 01-.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {isActionsOpen ? (
                  <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                      <Link
                        to={routesConfig.VEHICLES.CHOOSE_PAYMENT.replace(':id', vehicle.id)}
                        onClick={() => setIsActionsOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <PaymentIcon className="h-5 w-5 mr-2 text-primary" />
                        <span>{t('actionOpenPayment')}</span>
                      </Link>
                      <Link
                        to={routesConfig.VEHICLES.EDIT.replace(':id', vehicle.id)}
                        onClick={() => setIsActionsOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <EditVehicleIcon className="h-5 w-5 mr-2 text-primary" />
                        <span>{t('actionEditVehicle')}</span>
                      </Link>
                      <Link
                        to={routesConfig.VEHICLES.TEXT_ONLY.replace(':id', id)}
                        onClick={() => setIsActionsOpen(false)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <PrintIcon className="h-5 w-5 mr-2 text-primary" />
                        <span>{t('printMaintenanceBoook')}</span>
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          createTransferToken();
                          setIsActionsOpen(false);
                        }}
                        disabled={transferTokenLoading}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center disabled:opacity-50"
                      >
                        <TransferVehicleIcon className="w-5 h-5 mr-2 text-primary" />
                        <span>{t('actionTransferVehicle')}</span>
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="mt-2">
                <button onClick={scrollToReminders} className="float-right">
                  <div className="border border-primary p-2 rounded-md disabled:bg-gray-300 hover:bg-secondary hover:border-transparent xl:hidden">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-primary"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                  </div>
                </button>
                <button onClick={scrollToServiceEvents} className="float-right mr-2">
                  <div className="border border-primary  p-2 rounded-md disabled:bg-gray-300 hover:bg-secondary hover:border-transparent xl:hidden">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-primary"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                      />
                    </svg>
                  </div>
                </button>
                <div className="clear-both" />
              </div>
            </div>
          </div>
          <div className="mt-4 border-t border-gray-200 pt-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <span className={getLicenseBadgeClasses()}>
                  {licenseInfo.level !== 'GREEN' ? (
                    licenseInfo.level === 'NO_ACTIVE' ? (
                      <LockIcon className="w-6 h-6 mr-2" />
                    ) : (
                      <UrgentClockIcon className="w-6 h-6 mr-2" />
                    )
                  ) : null}
                  {licenseInfo.level === 'NO_ACTIVE' ? (
                    <>{t('noActiveLicense')}</>
                  ) : licenseInfo.daysLeft !== null && licenseInfo.daysLeft <= 60 ? (
                    <>{t('licenseExpiresInDays', { count: licenseInfo.daysLeft })}</>
                  ) : (
                    <>{t('licenseValidUntil', { date: licenseInfo.expireDateFormatted })}</>
                  )}
                </span>
              </div>
              {licenseInfo.level !== 'GREEN' ? (
                <div>
                  <Link
                    to={routesConfig.VEHICLES.CHOOSE_PAYMENT.replace(':id', vehicle.id)}
                    className="text-primary hover:text-secondary font-bold text-sm underline"
                  >
                    {t('buyLicense')}
                  </Link>
                </div>
              ) : null}
            </div>
          </div>
          <div className="mt-4 border-t border-gray-200 pt-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-primary flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              <button
                type="button"
                onClick={() => setIsAttachmentsOpen(true)}
                className="text-primary hover:text-secondary text-sm font-medium underline"
              >
                {t('attachmentsLink', { count: vehicle.attachments.length })}
              </button>
            </div>
          </div>
          <div className="max-w-screen-lg mt-4 mx-auto">
            <Image src={vehicle.photo} w={500} className="mx-auto rounded-md" />
          </div>
          <div className="mt-6">
            <Details columns={columns} data={vehicle} showMore={showDetails ? undefined : 4} />
            <button
              className="text-secondary cursor-pointer hover:text-primary"
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? t('hideDetails') : t('showDetails')}
            </button>
          </div>
        </div>
      </div>
      <VehicleAttachmentsModal
        isOpen={isAttachmentsOpen}
        onClose={() => setIsAttachmentsOpen(false)}
        vehicleId={vehicle.id}
        attachments={vehicle.attachments}
        hasActiveLicense={vehicle.hasActiveLicense}
        onRefetch={() => {
          refetch();
        }}
      />

      <div>
        <div className="bg-white py-4 px-3 shadow rounded-lg">
          <div className="grid grid-cols-3 items-center">
            <h2 className="text-lg col-span-2 leading-6 font-bold text-primary" ref={remindersRef}>
              {t('reminder')}
            </h2>

            {data.vehicle.hasActiveLicense ? (
              <div className="flex justify-end">
                <AddIconButton
                  to={routesConfig.REMINDER.CREATE.replace(':vehicleId', id)}
                  title={t('addReminder')}
                />
              </div>
            ) : null}
          </div>

          {data.vehicle.hasActiveLicense ? (
            <>
              {data.vehicle.reminders.length === 0
                ? emptyRemainder
                : data.vehicle.reminders.map((reminder) => (
                    <ReminderListItem
                      key={reminder.id}
                      reminder={reminder}
                      vehicleId={vehicle.id}
                    />
                  ))}
            </>
          ) : (
            <p>{t('pleaseReactivateLicense')}</p>
          )}
        </div>
      </div>

      <div>
        <div className="bg-white py-4 px-3 shadow rounded-lg">
          <div className="grid grid-cols-3 items-center">
            <h2
              className="text-lg col-span-2 leading-6 font-bold text-primary"
              ref={serviceEventsRef}
            >
              {t('serviceEvent')}
            </h2>
            {data.vehicle.hasActiveLicense ? (
              <div className="flex justify-end">
                <AddIconButton
                  to={routesConfig.SERVICE_EVENTS.CREATE.replace(':vehicleId', vehicle.id)}
                  title={t('addServiceEventButton')}
                />
              </div>
            ) : null}
          </div>

          {data.vehicle.hasActiveLicense ? (
            <>
              {data.vehicle.serviceEvents.length === 0
                ? emptyServiceEvent
                : data.vehicle.serviceEvents.map((serviceEvent) => (
                    <MaintenanceListItem
                      key={serviceEvent.id}
                      vehicleId={vehicle.id}
                      serviceEvent={serviceEvent}
                    />
                  ))}
            </>
          ) : (
            <p>{t('pleaseReactivateLicense')}</p>
          )}
        </div>
      </div>
    </div>
  );
};
